import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  articleData?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
  faqSchema?: { question: string; answer: string }[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  articleData,
  faqSchema,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, propType: 'name' | 'property' = 'name') => {
      let el = document.querySelector(`meta[${propType}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(propType, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setLink('canonical', canonicalUrl);

    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', ogType, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:locale', 'zh_CN', 'property');
    setMeta('og:site_name', '韩影深解', 'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (articleData) {
      if (articleData.publishedTime) setMeta('article:published_time', articleData.publishedTime);
      if (articleData.modifiedTime) setMeta('article:modified_time', articleData.modifiedTime);
      if (articleData.author) setMeta('article:author', articleData.author);
      if (articleData.section) setMeta('article:section', articleData.section);
    }

    const existingSchemas = document.querySelectorAll('script[data-schema="dynamic"]');
    existingSchemas.forEach((s) => s.remove());

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '韩影深解',
      url: 'https://hanyingshenjie.com',
      description,
      inLanguage: 'zh-CN',
    };

    const orgSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '韩影深解',
      url: 'https://hanyingshenjie.com',
      description: '韩国电影推荐与深度解析平台',
    };

    [websiteSchema, orgSchema].forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'dynamic');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    const webpageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: canonicalUrl,
      inLanguage: 'zh-CN',
      isPartOf: { '@id': 'https://hanyingshenjie.com/#website' },
    };
    const wpScript = document.createElement('script');
    wpScript.type = 'application/ld+json';
    wpScript.setAttribute('data-schema', 'dynamic');
    wpScript.textContent = JSON.stringify(webpageSchema);
    document.head.appendChild(wpScript);

    if (faqSchema && faqSchema.length > 0) {
      const faqJson = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqSchema.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      };
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.setAttribute('data-schema', 'dynamic');
      faqScript.textContent = JSON.stringify(faqJson);
      document.head.appendChild(faqScript);
    }

    if (ogType === 'article') {
      const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description,
        url: canonicalUrl,
        datePublished: articleData?.publishedTime,
        dateModified: articleData?.modifiedTime || articleData?.publishedTime,
        author: articleData?.author ? { '@type': 'Person', name: articleData.author } : undefined,
        inLanguage: 'zh-CN',
      };
      const artScript = document.createElement('script');
      artScript.type = 'application/ld+json';
      artScript.setAttribute('data-schema', 'dynamic');
      artScript.textContent = JSON.stringify(articleSchema);
      document.head.appendChild(artScript);
    }
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, articleData, faqSchema]);

  return null;
}